import { useState, useEffect, useCallback } from 'react';
import Sidebar from '../components/Sidebar';
import RolesTab from './tabs/roleTab';
import UsersTab from './tabs/usersTab';
import type { Role, User, Menu, CreateRoleDto, InviteUserDto } from './types';
import type { UpdateUserPayload } from './tabs/usersTab';
import {
  buildMenuQueryParams,
  extractMenuArray,
  getSelectedMenuContext,
  getUserVendorCategoryIdx,
  normalizeBoolean,
  normalizeMenuItems,
} from '../../../../../utils/menu';

const normalizeRoleType = (value: unknown, isAdmin: boolean): 'ADMIN' | 'GENERAL' => {
  if (value === 'ADMIN' || value === 'GENERAL') return value;
  return isAdmin ? 'ADMIN' : 'GENERAL';
};

const USER_INFO_UPDATED_EVENT = 'wedmin:user-info-updated';

export default function Permissions() {
  const [activeTab, setActiveTab] = useState<'roles' | 'users'>('roles');
  const [roles, setRoles] = useState<Role[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  
  // [1] 초기값을 빈 배열로 설정 (하드코딩 제거)
  const [menus, setMenus] = useState<Menu[]>([]); 
  const [isLoading, setIsLoading] = useState(false);
  const [inviteResult, setInviteResult] = useState<{ loginId: string; tempPassword: string } | null>(null);

  // [2] 메뉴 목록 API 호출 함수
  const fetchMenus = useCallback(async () => {
    try {
      const userStr = localStorage.getItem('userInfo');
      if (!userStr) return;

      const user = JSON.parse(userStr);
      const categoryIdx = getUserVendorCategoryIdx(user);
      const selectedContext = getSelectedMenuContext(user);

      const token = localStorage.getItem('accessToken');
      const query = buildMenuQueryParams({
        vendorCategoryIdx: categoryIdx,
        isGuest: selectedContext?.isGuestContext ?? false
      });
      const response = await fetch(`https://api.wedmin.biz/menu${query}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const result = await response.json();

      const menuRows = normalizeMenuItems(extractMenuArray(result));
      const mappedMenus: Menu[] = menuRows.map((m) => ({
        id: m.menuIdx,
        name: m.name,
        path: m.path || '',
        icon: m.icon || '',
        isActive: normalizeBoolean(m.isActive ?? true),
      }));
      setMenus(mappedMenus);

      // 메뉴가 비어있으면 선택된 컨텍스트의 메뉴로 fallback
      if (mappedMenus.length === 0 && selectedContext?.menus) {
        const fallbackMenus: Menu[] = normalizeMenuItems(selectedContext.menus).map((m) => ({
          id: m.menuIdx,
          name: m.name,
          path: m.path || '',
          icon: m.icon || '',
          isActive: normalizeBoolean(m.isActive ?? true),
        }));
        setMenus(fallbackMenus);
      }
    } catch (error) {
      console.error('Fetch Menus Error:', error);
    }
  }, []);

  const fetchRoles = useCallback(async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch('https://api.wedmin.biz/auth/permission/role', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const result = await response.json();
      if (result.status) {
        const roleItems = Array.isArray(result.data) ? result.data : [];
        const mappedRoles: Role[] = roleItems.map((item: any) => {
          const isAdmin = normalizeBoolean(item.isAdmin ?? item.is_admin_role);
          return {
            roleIdx: Number(item.roleIdx),
            name: item.name || '',
            description: item.description || '',
            accessibleMenuIds: Array.isArray(item.accessibleMenuIds)
              ? item.accessibleMenuIds.map((id: unknown) => Number(id)).filter((id: number) => Number.isFinite(id))
              : Array.isArray(item.menuIds)
                ? item.menuIds.map((id: unknown) => Number(id)).filter((id: number) => Number.isFinite(id))
                : [],
            userCount: Number(item.userCount ?? 0),
            isAdmin,
            roleType: normalizeRoleType(item.roleType, isAdmin)
          };
        });
        setRoles(mappedRoles);
      }
    } catch (error) {
      console.error('Fetch Roles Error:', error);
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch('https://api.wedmin.biz/auth/permission/user', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const result = await response.json();
      if (result.status) {
        const userItems = Array.isArray(result.data) ? result.data : [];
        const mappedUsers: User[] = userItems.map((item: any) => {
          const roleData = item.role || null;
          const roleIsAdmin = normalizeBoolean(item.isAdmin ?? roleData?.isAdmin ?? roleData?.is_admin_role);
          const role: Role | null = roleData
            ? {
                roleIdx: Number(roleData.roleIdx),
                name: roleData.name || '',
                description: roleData.description || '',
                accessibleMenuIds: Array.isArray(roleData.accessibleMenuIds)
                  ? roleData.accessibleMenuIds.map((id: unknown) => Number(id)).filter((id: number) => Number.isFinite(id))
                  : Array.isArray(roleData.menuIds)
                    ? roleData.menuIds.map((id: unknown) => Number(id)).filter((id: number) => Number.isFinite(id))
                    : [],
                userCount: Number(roleData.userCount ?? 0),
                isAdmin: normalizeBoolean(roleData.isAdmin ?? roleData.is_admin_role),
                roleType: normalizeRoleType(roleData.roleType, normalizeBoolean(roleData.isAdmin ?? roleData.is_admin_role))
              }
            : null;

          return {
            userIdx: Number(item.userIdx),
            name: item.name || '',
            loginId: item.loginId || '',
            email: item.email || '',
            phoneNumber: item.phoneNumber || '',
            role,
            roleIdx: item.roleIdx ?? role?.roleIdx ?? null,
            roleName: item.roleName ?? role?.name ?? null,
            isAdmin: roleIsAdmin,
            roleType: normalizeRoleType(item.roleType, roleIsAdmin),
            status: item.status || '',
            lastLoginDt: item.lastLoginDt || ''
          };
        });
        setUsers(mappedUsers);
      }
    } catch (error) {
      console.error('Fetch Users Error:', error);
    }
  }, []);

  // [3] 초기 로딩 시 메뉴와 역할 가져오기
  useEffect(() => {
    fetchRoles();
    fetchMenus(); 
    if (activeTab === 'users') fetchUsers();
  }, [fetchRoles, fetchMenus, fetchUsers, activeTab]);

  useEffect(() => {
    const handleUserInfoUpdate = () => {
      fetchMenus();
      fetchRoles();
      if (activeTab === 'users') {
        fetchUsers();
      }
    };

    window.addEventListener(USER_INFO_UPDATED_EVENT, handleUserInfoUpdate);
    return () => {
      window.removeEventListener(USER_INFO_UPDATED_EVENT, handleUserInfoUpdate);
    };
  }, [activeTab, fetchMenus, fetchRoles, fetchUsers]);

  // ... (handleAddRole, handleEditRole 등 나머지 핸들러 기존 유지)
  const handleAddRole = async (dto: CreateRoleDto) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch('https://api.wedmin.biz/auth/permission/role', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(dto)
      });
      
      const result = await response.json();
      if (response.ok) {
        alert('역할이 추가되었습니다.');
        fetchRoles();
      } else {
        alert(`추가 실패: ${result.message || '오류가 발생했습니다.'}`);
      }
    } catch (error) {
      console.error('Add Role Error:', error);
    }
  };
  const handleEditRole = async (roleIdx: number, dto: CreateRoleDto) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`https://api.wedmin.biz/auth/permission/role/${roleIdx}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(dto)
      });
      
      const result = await response.json();
      if (response.ok) {
        alert('역할이 수정되었습니다.');
        fetchRoles();
      } else {
        alert(`수정 실패: ${result.message || '오류가 발생했습니다.'}`);
      }
    } catch (error) {
      console.error('Edit Role Error:', error);
    }
  };
  const handleDeleteRole = async (roleIdx: number) => {
    if (!confirm('정말 이 역할을 삭제하시겠습니까?')) return;
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`https://api.wedmin.biz/auth/permission/role/${roleIdx}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const result = await response.json().catch(() => null);
      if (response.ok) {
        alert('역할이 삭제되었습니다.');
        fetchRoles();
      } else {
        alert(`삭제 실패: ${result?.message || '오류가 발생했습니다.'}`);
      }
    } catch (error) {
      console.error('Delete Role Error:', error);
      alert('삭제 중 오류가 발생했습니다.');
    }
  };
  const handleInviteUser = async (dto: InviteUserDto) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch('https://api.wedmin.biz/user/invite', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(dto)
      });
      
      const result = await response.json();
      if (response.ok) {
        const data = result?.data ?? result;
        const tempPassword = data?.tempPassword || data?.issuedTempPassword;
        if (tempPassword) {
          setInviteResult({ loginId: dto.loginId, tempPassword });
        } else {
          alert('직원이 초대되었습니다.');
        }
        fetchUsers();
      } else {
        alert(`초대 실패: ${result.message || '오류가 발생했습니다.'}`);
      }
    } catch (error) {
      console.error('Invite User Error:', error);
    }
  };

  const handleUpdateUser = async (payload: UpdateUserPayload) => {
    const token = localStorage.getItem('accessToken');
    try {
      // 1. 역할 변경 (PUT /auth/permission/user/{userIdx})
      if (payload.roleIdx !== undefined) {
        const roleRes = await fetch(`https://api.wedmin.biz/auth/permission/user/${payload.userIdx}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ roleIdx: payload.roleIdx })
        });
        if (!roleRes.ok) throw new Error('역할 변경 실패');
      }

      // 2. 상태 변경 (PATCH /user/{userIdx})
      if (payload.status !== undefined) {
        const statusRes = await fetch(`https://api.wedmin.biz/user/${payload.userIdx}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ status: payload.status })
        });
        if (!statusRes.ok) throw new Error('상태 변경 실패');
      }

      alert('사용자 정보가 수정되었습니다.');
      fetchUsers(); // 목록 갱신
    } catch (error) {
      console.error('Update User Error:', error);
      alert('수정 중 오류가 발생했습니다.');
    }
  };

  // [NEW] 사용자 삭제 (DELETE /user/{userIdx})
  const handleDeleteUser = async (userIdx: number) => {
    if (!confirm('정말 이 사용자를 삭제하시겠습니까?')) return;

    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`https://api.wedmin.biz/user/${userIdx}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        alert('사용자가 삭제되었습니다.');
        fetchUsers();
      } else {
        const result = await response.json();
        alert(`삭제 실패: ${result.message}`);
      }
    } catch (error) {
      console.error('Delete User Error:', error);
    }
  };
  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      <main className="flex-1 overflow-auto p-6">
        <h1 className="text-2xl font-bold text-gray-800 mb-6">사용자 관리</h1>

        <div className="flex gap-4 mb-6 border-b border-gray-200">
          <button onClick={() => setActiveTab('roles')} className={`px-4 py-2 font-medium transition-colors relative ${activeTab === 'roles' ? 'text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}>
            역할 관리
            {activeTab === 'roles' && <div className="absolute bottom-0 left-0 w-full h-0.5 bg-blue-600 rounded-t-full"></div>}
          </button>
          <button onClick={() => setActiveTab('users')} className={`px-4 py-2 font-medium transition-colors relative ${activeTab === 'users' ? 'text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}>
            사용자 관리
            {activeTab === 'users' && <div className="absolute bottom-0 left-0 w-full h-0.5 bg-blue-600 rounded-t-full"></div>}
          </button>
        </div>

        {activeTab === 'roles' ? (
          <RolesTab 
            roles={roles} 
            menus={menus} // [4] API로 가져온 메뉴 리스트 전달 (ID 15~28 포함됨)
            onAddRole={handleAddRole}
            onEditRole={handleEditRole}
            onDeleteRole={handleDeleteRole}
            onRefresh={fetchRoles}
          />
        ) : (
          <UsersTab users={users} roles={roles} onInviteUser={handleInviteUser}
          onUpdateUser={handleUpdateUser} // [NEW] 전달
            onDeleteUser={handleDeleteUser} // [NEW] 전달
          
          />
        )}

        {/* 초대 결과 모달 (복사 가능) */}
        {inviteResult && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-6">
              <h3 className="text-lg font-bold text-gray-800 mb-4">직원 초대 완료</h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">로그인 ID</label>
                  <div className="flex items-center gap-2">
                    <input type="text" readOnly value={inviteResult.loginId} className="flex-1 border rounded-lg px-3 py-2 text-sm bg-gray-50" />
                    <button onClick={() => { navigator.clipboard.writeText(inviteResult.loginId); alert('복사됨'); }}
                      className="px-3 py-2 text-sm bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 cursor-pointer"><i className="ri-file-copy-line"></i></button>
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">임시 비밀번호</label>
                  <div className="flex items-center gap-2">
                    <input type="text" readOnly value={inviteResult.tempPassword} className="flex-1 border rounded-lg px-3 py-2 text-sm bg-gray-50" />
                    <button onClick={() => { navigator.clipboard.writeText(inviteResult.tempPassword); alert('복사됨'); }}
                      className="px-3 py-2 text-sm bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 cursor-pointer"><i className="ri-file-copy-line"></i></button>
                  </div>
                </div>
              </div>
              <button onClick={() => setInviteResult(null)}
                className="w-full mt-5 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium cursor-pointer">확인</button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
